import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ClipboardList, Download, ChevronLeft, ChevronRight,
  LogIn, LogOut, UserPlus, Key, Shield, Database, AlertTriangle,
  ChevronDown, ChevronUp, Activity, CheckCircle, XCircle, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ACTION_ICONS = {
  'user.login': LogIn,
  'user.login_failed': AlertTriangle,
  'user.logout': LogOut,
  'user.created': UserPlus,
  'user.password_changed': Key,
  'user.password_reset': Key,
  'api_key.created': Key,
  'api_key.revoked': Key,
  'scan.performed': Shield,
  'scan.batch_performed': Shield,
  'watchlist.synced': Database,
  'watchlist.uploaded': Database,
  'user.mfa_reset': Shield,
};

const ACTION_LABELS = {
  'user.mfa_reset': 'MFA Reset',
};

const ACTION_COLORS = {
  'user.login': 'bg-emerald-100 text-emerald-800',
  'user.login_failed': 'bg-red-100 text-red-800',
  'user.logout': 'bg-slate-100 text-slate-700',
  'user.created': 'bg-blue-100 text-blue-800',
  'user.password_changed': 'bg-amber-100 text-amber-800',
  'user.password_reset': 'bg-orange-100 text-orange-800',
  'api_key.created': 'bg-violet-100 text-violet-800',
  'api_key.revoked': 'bg-amber-100 text-amber-800',
  'scan.performed': 'bg-cyan-100 text-cyan-800',
  'scan.batch_performed': 'bg-cyan-100 text-cyan-800',
  'watchlist.synced': 'bg-teal-100 text-teal-800',
  'watchlist.uploaded': 'bg-teal-100 text-teal-800',
  'user.mfa_reset': 'bg-rose-100 text-rose-800',
};

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'user.login', label: 'Login' },
  { value: 'user.login_failed', label: 'Login Failed' },
  { value: 'user.created', label: 'User Created' },
  { value: 'user.password', label: 'Password Changes' },
  { value: 'user.mfa_reset', label: 'MFA Resets' },
  { value: 'api_key', label: 'API Key' },
  { value: 'scan', label: 'Scans' },
  { value: 'watchlist', label: 'Watchlist' },
  { value: 'org', label: 'Organization' },
  { value: 'settings', label: 'Settings' },
];

const STATUS_TYPES = [
  { value: '', label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
];

export default function AuditLogsPage({ user, onLogout }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [blockedFilter, setBlockedFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const limit = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (actionFilter) params.action = actionFilter;
      if (actorFilter) params.actor_email = actorFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await axios.get(`${API}/audit-logs`, { headers: getHeaders(), params });
      let filteredLogs = data.logs;
      if (statusFilter) {
        filteredLogs = filteredLogs.filter(l => l.status === statusFilter);
      }
      if (blockedFilter) {
        filteredLogs = filteredLogs.filter(l => (l.details || {}).blocked === 'mfa_deadline_passed');
      }
      setLogs(filteredLogs);
      setTotal(data.total);
    } catch (err) {
      // Surface API failures so an empty table isn't mistaken for "no results".
      setLogs([]);
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load audit logs';
      toast.error(typeof detail === 'string' ? detail : 'Failed to load audit logs');
    }
    setLoading(false);
  }, [offset, actionFilter, actorFilter, startDate, endDate, statusFilter, blockedFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportLogs = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await axios.get(`${API}/audit-logs/export`, {
        headers: getHeaders(),
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to export audit logs';
      toast.error(typeof detail === 'string' ? detail : 'Failed to export audit logs');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString();
  };

  // Summary stats from current page
  const successCount = logs.filter(l => l.status === 'success').length;
  const failedCount = logs.filter(l => l.status === 'failed').length;
  const loginFailedCount = logs.filter(l => l.action === 'user.login_failed').length;

  return (
    <Layout user={user} onLogout={onLogout} activePage="audit-logs">
      <div className="space-y-6" data-testid="audit-logs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Audit Logs</h1>
            <p className="text-sm text-slate-500 mt-1">{total} total entries</p>
          </div>
          <Button onClick={exportLogs} variant="outline" data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="audit-stat-total">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total</p>
                  <p className="text-xl font-bold text-slate-800">{total.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="audit-stat-success">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Success (page)</p>
                  <p className="text-xl font-bold text-emerald-700">{successCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="audit-stat-failed">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Failed (page)</p>
                  <p className="text-xl font-bold text-red-700">{failedCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="audit-stat-login-failed">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Login Failures</p>
                  <p className="text-xl font-bold text-amber-700">{loginFailedCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="w-48">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Action Type</label>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setOffset(0); }}>
              <SelectTrigger data-testid="audit-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value || 'all'}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Status</label>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setOffset(0); }}>
              <SelectTrigger data-testid="audit-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value || 'all'}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px] max-w-xs">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Actor Email</label>
            <Input
              placeholder="Filter by email..."
              value={actorFilter}
              onChange={(e) => { setActorFilter(e.target.value); setOffset(0); }}
              data-testid="audit-actor-filter"
            />
          </div>
          <div className="w-40">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setOffset(0); }}
              data-testid="audit-start-date"
            />
          </div>
          <div className="w-40">
            <label className="text-xs font-medium text-slate-500 mb-1 block">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setOffset(0); }}
              data-testid="audit-end-date"
            />
          </div>
          {(actionFilter || actorFilter || statusFilter || startDate || endDate || blockedFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setActionFilter(''); setActorFilter(''); setStatusFilter(''); setStartDate(''); setEndDate(''); setBlockedFilter(false); setOffset(0); }}
              data-testid="audit-clear-filters"
            >
              Clear
            </Button>
          )}
        </div>

        {/* Quick filter chips */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setBlockedFilter(v => !v); setOffset(0); }}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${blockedFilter ? 'bg-rose-100 border-rose-300 text-rose-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            data-testid="audit-filter-mfa-blocked"
          >
            Blocked · MFA deadline
          </button>
        </div>

        {/* Logs Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-logs-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600 w-8"></th>
                  <th className="text-left p-3 font-medium text-slate-600">Timestamp</th>
                  <th className="text-left p-3 font-medium text-slate-600">Action</th>
                  <th className="text-left p-3 font-medium text-slate-600">Actor</th>
                  <th className="text-left p-3 font-medium text-slate-600">Status</th>
                  <th className="text-left p-3 font-medium text-slate-600">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-500">No audit logs found</td></tr>
                ) : logs.map((log, i) => {
                  const Icon = ACTION_ICONS[log.action] || ClipboardList;
                  const color = ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700';
                  const isExpanded = expandedRow === i;
                  const hasDetails = log.details && Object.keys(log.details).length > 0;
                  const hasResource = log.resource_type || log.resource_id;
                  return (
                    <React.Fragment key={`log-${i}`}>
                      <tr
                        className={`hover:bg-slate-50 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : i)}
                        data-testid={`audit-log-row-${i}`}
                      >
                        <td className="p-3 text-slate-400">
                          {(hasDetails || hasResource) ? (
                            isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          ) : null}
                        </td>
                        <td className="p-3 text-slate-600 whitespace-nowrap text-xs">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            {formatDate(log.timestamp)}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={`${color} text-xs`}>
                            <Icon className="w-3 h-3 mr-1" />
                            {ACTION_LABELS[log.action] || log.action}
                          </Badge>
                          {log.details?.summary && (
                            <p className="text-[11px] text-slate-500 mt-1" data-testid={`audit-log-summary-${i}`}>
                              {log.details.summary}
                            </p>
                          )}
                        </td>
                        <td className="p-3 text-slate-700 text-xs">{log.actor_email || '-'}</td>
                        <td className="p-3">
                          <Badge
                            variant={log.status === 'success' ? 'default' : 'destructive'}
                            className="text-xs"
                          >
                            {log.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-slate-500 text-xs font-mono">{log.ip_address || '-'}</td>
                      </tr>
                      {isExpanded && (hasDetails || hasResource) && (
                        <tr key={`${i}-detail`} data-testid={`audit-log-detail-${i}`}>
                          <td colSpan={6} className="px-6 py-3 bg-slate-50 border-b">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              {hasResource && (
                                <div>
                                  <span className="font-medium text-slate-600">Resource: </span>
                                  <span className="text-slate-700">{log.resource_type}/{log.resource_id || '-'}</span>
                                </div>
                              )}
                              {log.organization_id && (
                                <div>
                                  <span className="font-medium text-slate-600">Org ID: </span>
                                  <span className="font-mono text-slate-700">{log.organization_id}</span>
                                </div>
                              )}
                              {hasDetails && Object.entries(log.details).map(([k, v]) => (
                                <div key={k}>
                                  <span className="font-medium text-slate-600">{k}: </span>
                                  <span className="text-slate-700">{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between border-t p-3">
              <span className="text-sm text-slate-500">
                Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="audit-prev-page">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} data-testid="audit-next-page">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
